import {
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawRenderMode,
  TerraDrawSelectMode,
  type GeoJSONStoreFeatures,
} from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";
import { v4 as uuidv4 } from "uuid";
import {
  CIRCLE_DRAW_MODE,
  POLYGON_DRAW_MODE,
  READONLY_DRAW_MODE,
  RECTANGLE_DRAW_MODE,
  SELECT_DRAW_MODE,
  type DrawMode,
} from "./draw-modes";

const EDITABLE_POLYGON_FLAGS = {
  feature: {
    draggable: true,
    coordinates: {
      midpoints: { draggable: true },
      draggable: true,
      deletable: true,
    },
  },
};

const EDITABLE_CIRCLE_FLAGS = {
  feature: {
    draggable: true,
    coordinates: { draggable: true },
  },
};

const POINTER_DISTANCE = 15;

export const INACTIVE_COLOR = "#3bb2d0";
const ACTIVE_COLOR = "#fbb03b";

export type DrawEvent = "create" | "update" | "selectionchange" | "modechange";

type Listener = (payload: any) => void;

const TERRA_DRAW_MAX_PRECISION = 9;

function sanitizePosition(position: GeoJSON.Position): GeoJSON.Position {
  const round = (n: number) =>
    Math.round(n * 10 ** TERRA_DRAW_MAX_PRECISION) /
    10 ** TERRA_DRAW_MAX_PRECISION;
  return [round(position[0]), round(position[1])];
}


function toDrawablePolygon(coordinates: GeoJSON.Position[][]): GeoJSON.Polygon {
  return {
    type: "Polygon",
    coordinates: [coordinates[0].map(sanitizePosition)],
  };
}

function toDrawableGeometries(
  feature: GeoJSON.Feature
): GeoJSON.Polygon[] | GeoJSON.LineString[] | GeoJSON.Point[] | [] {
  const geometry = feature.geometry;
  switch (geometry.type) {
    case "Polygon":
      return [toDrawablePolygon(geometry.coordinates)];
    case "MultiPolygon":
      return geometry.coordinates.map(toDrawablePolygon);
    default:
      return [];
  }
}

// Terra Draw is slow per vertex when editing (~2.3s per drag step at 20k) and crashes past ~100k points.
export const MAX_EDITABLE_VERTICES = 2_000;

// Terra Draw re-renders every feature on each interaction, so a clip over this total is shown read-only.
const MAX_EDITABLE_TOTAL_VERTICES = 50_000;

// Cached per geometry object: a drag rebuilds the features array but keeps untouched shapes' geometry.
const vertexCounts = new WeakMap<GeoJSON.Geometry, number>();

export function countVertices(geometry: GeoJSON.Geometry): number {
  const cached = vertexCounts.get(geometry);
  if (cached !== undefined) return cached;
  let count = 0;
  switch (geometry.type) {
    case "Polygon":
      count = geometry.coordinates.reduce((n, ring) => n + ring.length, 0);
      break;
    case "MultiPolygon":
      count = geometry.coordinates.reduce(
        (n, polygon) => n + polygon.reduce((m, ring) => m + ring.length, 0),
        0
      );
      break;
  }
  vertexCounts.set(geometry, count);
  return count;
}

export function isTooDetailedToEdit(feature: GeoJSON.Feature): boolean {
  return countVertices(feature.geometry) > MAX_EDITABLE_VERTICES;
}

// The marker is set by the backend converter and kept through saves.
const UPLOADED_SHAPE_SOURCE = "upload";

export function isUploadedShape(feature: GeoJSON.Feature): boolean {
  return feature.properties?.source === UPLOADED_SHAPE_SOURCE;
}

export function isClipTooHeavyToEdit(features: GeoJSON.Feature[]): boolean {
  let total = 0;
  for (const f of features) {
    total += countVertices(f.geometry);
    if (total > MAX_EDITABLE_TOTAL_VERTICES) return true;
  }
  return false;
}

// The single read-only rule; the editor UI uses it too so its labels match the map.
export function partitionViewOnly(features: GeoJSON.Feature[]): {
  readOnly: GeoJSON.Feature[];
  editable: GeoJSON.Feature[];
  clipTooHeavy: boolean;
} {
  const clipTooHeavy = isClipTooHeavyToEdit(features);
  const readOnly: GeoJSON.Feature[] = [];
  const editable: GeoJSON.Feature[] = [];
  for (const f of features) {
    (clipTooHeavy || isUploadedShape(f) || isTooDetailedToEdit(f)
      ? readOnly
      : editable
    ).push(f);
  }
  return { readOnly, editable, clipTooHeavy };
}

const READONLY_SOURCE = "boundary-readonly";
const READONLY_FILL_LAYER = "boundary-readonly-fill";
const READONLY_LINE_LAYER = "boundary-readonly-line";

const EDITABLE_DRAW_MODES: DrawMode[] = [
  POLYGON_DRAW_MODE,
  RECTANGLE_DRAW_MODE,
  CIRCLE_DRAW_MODE,
];

function drawModeFor(feature: GeoJSON.Feature): DrawMode {
  const mode = feature.properties?.mode;
  return EDITABLE_DRAW_MODES.includes(mode) ? mode : POLYGON_DRAW_MODE;
}

function polygonStyles(color: string) {
  return {
    fillColor: color as `#${string}`,
    fillOpacity: 0.1,
    outlineColor: color as `#${string}`,
    outlineWidth: 2,
  };
}


export class BoundaryDraw {
  private terraDraw: TerraDraw | null = null;
  private map: any = null;
  private mode: DrawMode = SELECT_DRAW_MODE;
  private listeners = new Map<DrawEvent, Set<Listener>>();
  private drawIds = new Map<string, string[]>();
  private pending: GeoJSON.Feature[] = [];
  private pendingRestore: GeoJSONStoreFeatures[] = [];
  private applying = false;
  private detachStyleListener: (() => void) | null = null;
  private geometries = new Map<string, string>();
  private selectedDrawId: string | null = null;
  // Keyed by app id.
  private readonlyFeatures = new Map<string, GeoJSON.Feature>();

  attach(mapRefOrMap: any) {
    const map =
      typeof mapRefOrMap?.getMap === "function"
        ? mapRefOrMap.getMap()
        : mapRefOrMap;
    if (!map || this.map === map) return;
    if (this.map) this.detach();

    this.map = map;
    this.terraDraw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      idStrategy: {
        isValidId: (id) => typeof id === "string" && id.length > 0,
        getId: () => uuidv4(),
      },
      modes: [
        new TerraDrawSelectMode({
          pointerDistance: POINTER_DISTANCE,
          flags: {
            [POLYGON_DRAW_MODE]: EDITABLE_POLYGON_FLAGS,
            [RECTANGLE_DRAW_MODE]: EDITABLE_POLYGON_FLAGS,
            [CIRCLE_DRAW_MODE]: EDITABLE_CIRCLE_FLAGS,
          },
          styles: {
            selectedPolygonColor: ACTIVE_COLOR,
            selectedPolygonFillOpacity: 0.1,
            selectedPolygonOutlineColor: ACTIVE_COLOR,
            selectedPolygonOutlineWidth: 2,
            midPointColor: ACTIVE_COLOR,
            selectionPointColor: ACTIVE_COLOR,
          },
        }),
        new TerraDrawPolygonMode({ styles: polygonStyles(INACTIVE_COLOR) }),
        new TerraDrawRectangleMode({ styles: polygonStyles(INACTIVE_COLOR) }),
        new TerraDrawCircleMode({ styles: polygonStyles(INACTIVE_COLOR) }),
        new TerraDrawRenderMode({
          modeName: READONLY_DRAW_MODE,
          styles: {
            polygonFillColor: INACTIVE_COLOR,
            polygonFillOpacity: 0.1,
            polygonOutlineColor: INACTIVE_COLOR,
            polygonOutlineWidth: 2,
          },
        }),
      ],
    });

    this.terraDraw.on("finish", (id, context) => {
      if (context.action !== "draw") return;
      const feature = this.readFeature(id);
      if (!feature) return;
      this.drawIds.set(String(id), [String(id)]);
      this.geometryChanged(feature);
      this.emit("create", { features: [feature] });
      setTimeout(() => this.changeMode(SELECT_DRAW_MODE), 0);
    });

    this.terraDraw.on("change", (ids, type) => {
      if (type !== "update" || this.applying) return;
      const features = ids
        .filter((id) => this.isTracked(id))
        .map((id) => this.readFeature(id))
        .filter((feature): feature is GeoJSON.Feature =>
          Boolean(feature) && this.geometryChanged(feature!)
        );
      if (features.length) this.emit("update", { features });
    });

    this.terraDraw.on("select", (id) => {
      this.selectedDrawId = String(id);
      const feature = this.readFeature(id);
      this.emit("selectionchange", { features: feature ? [feature] : [] });
    });

    this.terraDraw.on("deselect", () => {
      this.selectedDrawId = null;
      this.emit("selectionchange", { features: [] });
    });

    this.startDraw();

    const onStyleData = () => {
      this.redraw();
      // A basemap style swap drops custom layers, so put the read-only shapes back.
      if (this.readonlyFeatures.size && !map.getSource(READONLY_SOURCE)) {
        this.renderReadonly();
      }
    };
    map.on("styledata", onStyleData);
    this.detachStyleListener = () => map.off("styledata", onStyleData);
  }


  private startDraw() {
    if (!this.terraDraw || !this.map) return false;
    if (this.terraDraw.enabled) return true;
    const map = this.map;

    if (!map.isStyleLoaded()) {
      map.once("idle", () => this.startDraw());
      return false;
    }

    const original = map.isStyleLoaded;
    map.isStyleLoaded = () => true;
    try {
      this.terraDraw.start();
      this.terraDraw.setMode(this.mode);
    } catch (err) {
      console.error("[boundary-draw] failed to start Terra Draw:", err);
      this.stopDraw();
      return false;
    } finally {
      map.isStyleLoaded = original;
    }

    const buffered = this.pending;
    this.pending = [];
    if (buffered.length) this.add(buffered);

    if (this.pendingRestore.length) {
      const restore = this.pendingRestore;
      this.pendingRestore = [];
      this.addDrawFeatures(restore);
    }
    return true;
  }

  detach() {
    this.detachStyleListener?.();
    this.detachStyleListener = null;
    this.removeReadonlyLayers();
    this.stopDraw();
    this.terraDraw = null;
    this.map = null;
    this.drawIds.clear();
  }

  // Add a clip's features in one call: Terra Draw re-renders all features on each addFeatures (N^2 otherwise).
  add(featureOrFeatures: GeoJSON.Feature | GeoJSON.Feature[]) {
    const features = Array.isArray(featureOrFeatures)
      ? featureOrFeatures
      : [featureOrFeatures];

    // Read-only shapes are routed first so they don't wait for Terra Draw to start.
    const { readOnly, editable } = partitionViewOnly(features);
    for (const feature of readOnly) {
      const appId = String(feature.id ?? uuidv4());
      if (this.drawIds.has(appId)) this.delete([appId]);
      this.readonlyFeatures.set(appId, { ...feature, id: appId });
    }
    let readonlyChanged = readOnly.length > 0;
    for (const feature of editable) {
      if (this.readonlyFeatures.delete(String(feature.id))) readonlyChanged = true;
    }
    if (readonlyChanged) this.renderReadonly();

    if (!this.terraDraw?.enabled) {
      this.pending.push(...editable);
      return;
    }

    const entries: {
      appId: string;
      feature: GeoJSON.Feature;
      drawIds: string[];
    }[] = [];
    const drawFeatures: GeoJSONStoreFeatures[] = [];
    for (const feature of editable) {
      const appId = String(feature.id ?? uuidv4());
      const geometries = toDrawableGeometries(feature);
      if (!geometries.length) {
        console.warn(
          `[boundary-draw] feature ${appId} has unsupported geometry ` +
            `"${feature.geometry.type}" and is not shown on the map`,
        );
        continue;
      }

      if (this.drawIds.has(appId)) this.delete([appId]);

      const mode =
        geometries.length > 1 ? READONLY_DRAW_MODE : drawModeFor(feature);
      const parts = geometries.map((geometry, i) => ({
        id: geometries.length > 1 ? `${appId}:${i}` : appId,
        type: "Feature" as const,
        geometry,
        properties: { ...feature.properties, mode },
      }));
      drawFeatures.push(...(parts as GeoJSONStoreFeatures[]));
      entries.push({ appId, feature, drawIds: parts.map((p) => p.id) });
    }
    if (!drawFeatures.length) return;

    this.applying = true;
    let acceptedIds: string[];
    try {
      acceptedIds = this.addDrawFeatures(drawFeatures);
    } finally {
      this.applying = false;
    }
    const accepted = new Set(acceptedIds);

    for (const { appId, feature, drawIds } of entries) {
      const kept = drawIds.filter((id) => accepted.has(id));
      if (!kept.length) continue;
      this.drawIds.set(appId, kept);
      this.geometries.set(appId, JSON.stringify(feature.geometry));
    }
  }

  delete(ids: string[]) {
    // Not ids.some(...): it stops at the first match and would leave the other shapes on the map.
    let readonlyChanged = false;
    for (const id of ids) {
      if (this.readonlyFeatures.delete(id)) readonlyChanged = true;
    }
    if (readonlyChanged) this.renderReadonly();
    if (!this.terraDraw) {
      this.pending = this.pending.filter((f) => !ids.includes(String(f.id)));
      return;
    }
    const drawIds = ids.flatMap((id) => this.drawIds.get(id) ?? []);
    if (this.selectedDrawId && drawIds.includes(this.selectedDrawId)) {
      this.selectedDrawId = null;
    }
    if (drawIds.length) this.terraDraw.removeFeatures(drawIds);
    ids.forEach((id) => {
      this.drawIds.delete(id);
      this.geometries.delete(id);
    });
  }

  deleteAll() {
    this.pending = [];
    if (this.readonlyFeatures.size) {
      this.readonlyFeatures.clear();
      this.renderReadonly();
    }
    this.drawIds.clear();
    this.geometries.clear();
    this.selectedDrawId = null;
    try {
      this.terraDraw?.clear();
    } catch {
      /* sources already removed */
    }
  }

  changeMode(mode: DrawMode) {
    this.mode = mode;
    this.terraDraw?.setMode(mode);
    this.emit("modechange", { mode });
  }

  getMode(): DrawMode {
    return this.mode;
  }

  select(appId: string): boolean {
    if (!this.terraDraw) return false;
    const drawIds = this.drawIds.get(appId);

    if (this.selectedDrawId) {
      try {
        this.terraDraw.deselectFeature(this.selectedDrawId);
      } catch {
        /* already gone */
      }
      this.selectedDrawId = null;
    }

    if (!drawIds || drawIds.length !== 1) return false;
    this.changeMode(SELECT_DRAW_MODE);
    try {
      this.terraDraw.selectFeature(drawIds[0]);
      this.selectedDrawId = drawIds[0];
    } catch (err) {
      console.warn("[boundary-draw] could not select feature:", err);
      return false;
    }
    return true;
  }

  on(event: DrawEvent, listener: Listener) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(listener);
    return () => this.off(event, listener);
  }

  off(event: DrawEvent, listener: Listener) {
    this.listeners.get(event)?.delete(listener);
  }

  private addDrawFeatures(features: GeoJSONStoreFeatures[]): string[] {
    const validations = this.terraDraw?.addFeatures(features) ?? [];
    const accepted: string[] = [];
    validations.forEach((validation) => {
      if (validation.valid) {
        accepted.push(String(validation.id));
        return;
      }
      console.error(
        `[boundary-draw] Terra Draw rejected feature ${String(validation.id)}: ` +
          (validation.reason ?? "no reason given"),
      );
    });
    return accepted;
  }

  private stopDraw() {
    try {
      this.terraDraw?.stop();
    } catch {
      /* sources already removed */
    }
  }

  private redraw() {
    if (!this.terraDraw || !this.map) return;

    if (!this.terraDraw.enabled) {
      this.startDraw();
      return;
    }

    try {
      if (this.map.getSource("td-polygon")) return;
    } catch {
      return;
    }

    const trackedIds = new Set([...this.drawIds.values()].flat());
    this.pendingRestore = this.terraDraw
      .getSnapshot()
      .filter((feature) => trackedIds.has(String(feature.id)));

    this.stopDraw();
    this.startDraw();
  }

  // Rebuilt on every change: a reused source with setData stops redrawing in the explorer map.
  private renderReadonly() {
    const map = this.map;
    if (!map || !map.style) return;
    this.removeReadonlyLayers();
    if (!this.readonlyFeatures.size) return;
    map.addSource(READONLY_SOURCE, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [...this.readonlyFeatures.values()],
      },
    });
    map.addLayer({
      id: READONLY_FILL_LAYER,
      type: "fill",
      source: READONLY_SOURCE,
      paint: { "fill-color": INACTIVE_COLOR, "fill-opacity": 0.1 },
    });
    map.addLayer({
      id: READONLY_LINE_LAYER,
      type: "line",
      source: READONLY_SOURCE,
      paint: {
        "line-color": INACTIVE_COLOR,
        "line-width": 2,
        "line-dasharray": [3, 2],
      },
    });
  }

  private removeReadonlyLayers() {
    const map = this.map;
    if (!map || !map.style) return;
    for (const id of [READONLY_LINE_LAYER, READONLY_FILL_LAYER]) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
    if (map.getSource(READONLY_SOURCE)) map.removeSource(READONLY_SOURCE);
  }

  private readFeature(id: string | number): GeoJSON.Feature | null {
    const feature = this.terraDraw?.getSnapshotFeature(id);
    if (!feature) return null;
    return { ...feature, id: this.appIdFor(id) } as GeoJSON.Feature;
  }

  private geometryChanged(feature: GeoJSON.Feature): boolean {
    const id = String(feature.id);
    const geometry = JSON.stringify(feature.geometry);
    if (this.geometries.get(id) === geometry) return false;
    this.geometries.set(id, geometry);
    return true;
  }

  private isTracked(drawId: string | number): boolean {
    for (const ids of this.drawIds.values()) {
      if (ids.includes(String(drawId))) return ids.length === 1;
    }
    return false;
  }

  private appIdFor(drawId: string | number): string {
    for (const [appId, ids] of this.drawIds) {
      if (ids.includes(String(drawId))) return appId;
    }
    return String(drawId);
  }

  private emit(event: DrawEvent, payload: any) {
    this.listeners.get(event)?.forEach((listener) => listener(payload));
  }
}
